import objStr from 'obj-str';
import React, { useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Portal } from '@/components/Portal';
import { useBrowserLayoutEffect } from '@/hooks/useBrowserLayoutEffect';
import { useOverflowHidden } from '@/hooks/useOverflowHidden';
import { useClickOutside } from '@/hooks/useClickOutside';
import styles from './SlidePane.module.scss';

interface ISlidePane {
  children: React.ReactNode;
  title?: string;
  isOpen?: boolean;
  side?: 'left' | 'right';
  size?: 'auto';
  animationTime?: number;
  onHideSlidePane: AppCallbackFn;
}

interface RestProps {
  [x: string]: any;
}

export const SlidePane = ({
  title,
  children,
  isOpen,
  onHideSlidePane,
  side = 'right',
  size,
  animationTime = 350,
  ...restProps
}: ISlidePane & RestProps) => {
  const paneRef = useRef(null);
  const [showChild, setShowChild] = useState(false);
  const [isSlidepaneOpen, setIsSlidePaneOpen] = useState<boolean>(isOpen ?? false);

  // setTimout allows animations to finish before triggereing a re-render
  const closePane = async () => {
    if (animationTime > 0) {
      // setTimout() to forcefully disable React's 'auto batching' mechanism for state updates
      setTimeout(() => setShowChild(() => false), 0);
      setTimeout(() => setIsSlidePaneOpen(() => false), 300);
      setTimeout(() => onHideSlidePane(), 350);
    } else {
      setIsSlidePaneOpen(false);
      setShowChild(false);
      onHideSlidePane();
    }
  };

  useOverflowHidden(isSlidepaneOpen);
  useClickOutside(paneRef, closePane);

  useBrowserLayoutEffect(() => {
    setIsSlidePaneOpen(() => isOpen ?? false);
  }, [isOpen]);

  useBrowserLayoutEffect(() => {
    // setTimout(0) to forcefully disable React's 'auto batching' mechanism for state updates
    isSlidepaneOpen && setTimeout(() => setShowChild(() => true), 0);
    !isSlidepaneOpen && setShowChild(() => false);
  }, [isSlidepaneOpen]);

  const classNames = objStr({
    [styles.SlidePane]: true,
    [styles.SlidePaneLeft]: side === 'left',
    [styles.SlidePaneSizeAuto]: size === 'auto',
    [styles.SlidePaneIsOpen]: showChild,
    [restProps?.openClassName]: showChild && !!restProps.openClassName,
    [restProps?.className]: !!restProps?.className
  });

  return (
    <>
      <Portal selector="#portal">
        {isSlidepaneOpen && (
          <div className={classNames} ref={paneRef}>
            {title && (
              <Row>
                <Col>
                  <h1 className="h1 my-2 ml-2">{title}</h1>
                </Col>
                <Col xs="auto">
                  <button className={`${styles.btnClose} btn btn-link close`} onClick={closePane}>
                    <span aria-hidden="true">×</span>
                  </button>
                </Col>
              </Row>
            )}
            {showChild && (
              <Row>
                <Col>{children}</Col>
              </Row>
            )}
          </div>
        )}
      </Portal>

      {isSlidepaneOpen && (
        <Portal selector="body">
          <div className={`${styles.backdrop} ${showChild ? styles.fadeIn : styles.fadeOut}`}></div>
        </Portal>
      )}
    </>
  );
};
